import { User } from "../types/auth";

export interface NavigationItemConfig {
  id: string;
  name: string;
  route: string;
  module: string;
  icon?: React.ComponentType<{ className?: string }>;
  showForSuperAdmin?: boolean; // If true, shows as direct item for superadmin
  showInNested?: boolean; // If true, shows in nested group for regular users
}

export interface NestedGroupConfig {
  id: string;
  name: string;
  route: string;
  items: NavigationItemConfig[];
  visibilityCheck?: (
    userModules: Set<string>,
    isSuperAdmin: boolean
  ) => boolean;
}

/**
 * Extract unique modules from user permissions
 */
export const getUserModules = (permissions: string[]): Set<string> => {
  return new Set(permissions.map((perm) => perm.split(".")[0]));
};

/**
 * Check if user is superadmin
 */
export const isSuperAdmin = (role?: string): boolean => {
  return role === "Super Admin";
};

/**
 * Get visible navigation items based on user permissions and role
 */
export const getVisibleNavigationItems = (
  items: NavigationItemConfig[],
  userModules: Set<string>,
  isSuperAdminUser: boolean
): NavigationItemConfig[] => {
  return items.filter((item) => {
    // Check if user has permission for this module
    const hasPermission = userModules.has(item.module);

    // For superadmin, check showForSuperAdmin flag
    if (isSuperAdminUser) {
      return hasPermission && (item.showForSuperAdmin ?? false);
    }

    // For regular users, check showInNested flag
    return hasPermission && (item.showInNested ?? true);
  });
};

/**
 * Check if nested group should be visible
 */
export const shouldShowNestedGroup = (
  group: NestedGroupConfig,
  userModules: Set<string>,
  isSuperAdminUser: boolean
): boolean => {
  // Use custom visibility check if provided
  if (group.visibilityCheck) {
    return group.visibilityCheck(userModules, isSuperAdminUser);
  }

  // Default: show if user has permission for any item in the group
  return group.items.some((item) => {
    const hasPermission = userModules.has(item.module);
    if (isSuperAdminUser) {
      return hasPermission && !(item.showForSuperAdmin ?? false);
    }
    return hasPermission && (item.showInNested ?? true);
  });
};

/**
 * Get visible items for a nested group
 */
export const getVisibleNestedItems = (
  group: NestedGroupConfig,
  userModules: Set<string>,
  isSuperAdminUser: boolean
): NavigationItemConfig[] => {
  return group.items.filter((item) => {
    const hasPermission = userModules.has(item.module);

    // For superadmin, exclude items that should be direct
    if (isSuperAdminUser) {
      return hasPermission && !(item.showForSuperAdmin ?? false);
    }

    // For regular users, include items that should be nested
    return hasPermission && (item.showInNested ?? true);
  });
};
